import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import styles from './PortfolioEntries.module.css';

interface Entry {
  id: string;
  month: string;
  rocnik: string | null;
  title: string;
  description: string;
  image: string | null;
  created_at: string;
}

export const PortfolioEntries = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    month: new Date().toLocaleString('cs-CZ', { month: 'long' }),
    rocnik: '',
    title: '',
    description: '',
  });

  // Načtení záznamů
  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('school_portfolio_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError(
          'Tabulka neexistuje nebo chyba v připojení. Prosím vytvoř tabulku v Supabase.'
        );
        setEntries([]);
      } else {
        setEntries(data || []);
      }
    } catch (err) {
      setError('Chyba při načítání záznamů. Zkontroluj připojení k Supabase.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('portfolio-images')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return null;
      }

      const { data } = supabase.storage.from('portfolio-images').getPublicUrl(fileName);
      return data.publicUrl;
    } catch (err) {
      console.error('Upload error:', err);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.month.trim() || !formData.rocnik.trim() || !formData.title.trim() || !formData.description.trim()) {
      setError('Vyplň všechna pole!');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      let imageUrl: string | null = null;
      
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
        if (!imageUrl) {
          setError('Chyba při nahrávání obrázku. Pokud chceš, můžeš pokračovat bez něj.');
          imageUrl = null;
        }
      }

      const { error: insertError } = await supabase.from('school_portfolio_entries').insert([
        {
          month: formData.month,
          rocnik: formData.rocnik,
          title: formData.title,
          description: formData.description,
          image: imageUrl,
        },
      ]);

      if (insertError) {
        setError('Chyba při ukládání. Zkontroluj konfiguraci Supabase.');
        console.error(insertError);
      } else {
        setSuccess(true);
        setFormData({
          month: new Date().toLocaleString('cs-CZ', { month: 'long' }),
          rocnik: '',
          title: '',
          description: '',
        });
        setImageFile(null);
        setImagePreview(null);
        await loadEntries();
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      setError('Neočekávaná chyba při ukládání.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Opravdu chceš smazat tento záznam?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('school_portfolio_entries')
        .delete()
        .eq('id', id);

      if (deleteError) {
        setError('Chyba při mazání.');
        console.error(deleteError);
      } else {
        await loadEntries();
      }
    } catch (err) {
      setError('Chyba při mazání.');
      console.error(err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={styles.portfolioEntries}>
      <h1 className={styles.title}>Zápisy do Portfolia</h1>

      <div className={styles['form-section']}>
        <h2 className={styles['form-title']}>Přidat nový zápis</h2>

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>Zápis byl úspěšně uložen! ✅</div>}

        <form onSubmit={handleSubmit}>
          <div className={styles['form-group']}>
            <label htmlFor="month" className={styles.label}>
              Měsíc
            </label>
            <input
              id="month"
              type="text"
              name="month"
              value={formData.month}
              onChange={handleChange}
              className={styles.input}
              placeholder="Např. Leden, Únor..."
              disabled={uploading}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="rocnik" className={styles.label}>
              Ročník
            </label>
            <input
              id="rocnik"
              type="text"
              name="rocnik"
              value={formData.rocnik}
              onChange={handleChange}
              className={styles.input}
              placeholder="Např. 1., 2., 3."
              disabled={uploading}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="title" className={styles.label}>
              Nadpis
            </label>
            <input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={styles.input}
              placeholder="Např. Naučil jsem se React Hooks"
              disabled={uploading}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="description" className={styles.label}>
              Popis
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              placeholder="Popiš co nového ses naučil nebo jaký projekt jsi udělal..."
              disabled={uploading}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="image" className={styles.label}>
              Obrázek (volitelné)
            </label>
            <input
              id="image"
              type="file"
              name="image"
              onChange={handleImageChange}
              className={styles.input}
              accept="image/*"
              disabled={uploading}
            />
            {imagePreview && (
              <div className={styles['image-preview']}>
                <img src={imagePreview} alt="Preview" />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className={styles['remove-image']}
                >
                  ✕ Odebrat
                </button>
              </div>
            )}
          </div>

          <div className={styles['button-group']}>
            <button type="submit" className={styles['submit-btn']} disabled={uploading}>
              {uploading ? 'Nahrávám...' : 'Uložit zápis'}
            </button>
          </div>
        </form>
      </div>

      <div className={styles['entries-section']}>
        <h2 className={styles['entries-title']}>Moje zápisy ({entries.length})</h2>

        {entries.length === 0 ? (
          <div className={styles['empty-state']}>
            {loading ? 'Načítám...' : 'Zatím žádné zápisy. Přidej svůj první!'}
          </div>
        ) : (
          <div className={styles['entries-list']}>
            {entries.map((entry) => (
              <div key={entry.id} className={styles['entry-card']}>
                {entry.image && (
                  <div className={styles['entry-image']}>
                    <img src={entry.image} alt={entry.title} />
                  </div>
                )}
                <div className={styles['entry-content']}>
                  <div className={styles['entry-header']}>
                    <div>
                      <h3 className={styles['entry-title']}>{entry.title}</h3>
                      <span className={styles['entry-month']}>
                        {entry.month}
                        {entry.rocnik ? ` • ${entry.rocnik}` : ''}
                      </span>
                    </div>
                    <span className={styles['entry-date']}>{formatDate(entry.created_at)}</span>
                  </div>
                  <p className={styles['entry-description']}>{entry.description}</p>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className={styles['delete-btn']}
                  >
                    Smazat
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
